// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/campaign.test.tsx
// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.campaign.spec.ts
import {
  ArrowsInSimple,
  ArrowsOutSimple,
  CircleNotch,
  UserPlus,
} from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  Flex,
  Text,
  TextArea,
  Tooltip,
} from "@radix-ui/themes";
// eslint-disable-next-line import/named
import { useQueryClient } from "@tanstack/react-query";
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import cx from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ImperativePanelGroupHandle,
  Panel,
  PanelGroup,
} from "react-resizable-panels";

import { type ChatArtifact } from "./artifactTypes";
import {
  buildProjectChatPath,
  buildProjectFeedPath,
  buildProjectReviewPath,
  parseProjectReviewRouteSearch,
} from "./askPunttPaths";
import { campaignSearch, type CampaignSearch } from "./campaignSearch";
import AskPunttChat from "./chat";
import { collectChatArtifacts } from "./chatUtils";
import { CampaignChatDetailHeader } from "./components/CampaignChatDetailHeader";
import { CampaignChatList } from "./components/CampaignChatList";
import { CampaignChatPrivacyBanner } from "./components/CampaignChatPrivacyBanner";
import { CampaignDocsPanel } from "./components/CampaignDocsPanel";
import { CampaignNewChatDraft } from "./components/CampaignNewChatDraft";
import { CampaignReviewPane } from "./components/CampaignReviewPane";
import { type CanvasContent } from "./components/canvas/types";
import { CanvasPane } from "./components/CanvasPane";
import { ChatArtifactsPanel } from "./components/ChatArtifactsPanel";
import { CollapsedPaneLabel } from "./components/CollapsedPaneLabel";
import {
  PANE_HEADER_HEIGHT_CLASS,
  PANE_HEADER_ICON_SIZE,
  PANE_HEADER_SIDE_INSET_CLASS,
  PANE_HEADER_SURFACE_CLASS,
  PaneHeaderIconButton,
} from "./components/PaneHeader";
import { ProjectBreadcrumbTitle } from "./components/ProjectBreadcrumbTitle";
import { ProjectInviteDialog } from "./components/ProjectInviteDialog";
import { ProjectReviewsPanel } from "./components/ProjectReviewsPanel";
import { ResizeHandle } from "./components/ResizeHandle";
import { ResizeHintTooltip } from "./components/ResizeHintTooltip";
import { useContainerWidth } from "./hooks/useContainerWidth";
import { usePersistedLayout } from "./hooks/usePersistedLayout";
import { useUiPrefs } from "./hooks/useUiPrefs";
import { isArtifactUnread } from "./utils/askPunttUnread";
import {
  enrichProjectReviewsFromFiles,
  projectReferenceDocumentRows,
} from "./utils/projectContextDocuments";
import {
  FEED_AGENT_POLL_INTERVAL_MS,
  FEED_POLL_INTERVAL_MS,
  resolveFeedTimelinePollIntervalMs,
  timelineHasPendingAssistant,
} from "./utils/projectFeedTimeline";
import {
  FEED_RAIL_DEFAULT,
  FEED_RAIL_MINS,
  PROJECT_RAIL_WIDTH_PX,
  RESIZE_LAYOUT_MIN_WIDTH_PX,
  REVIEW_CHAT_FALLBACK_PX,
  reviewWorkspaceMinWidth,
  reviewWorkspaceRowStyle,
} from "./utils/projectWorkspaceLayout";
import {
  isAgentTurnEvent,
  renderProjectFeedCard,
} from "./utils/renderProjectFeedCard";
import { collectUsedConnectors } from "./utils/usedConnectors";

import { AvatarWithInitials } from "../../components/AvatarWithInitials";
import {
  BreadcrumbHeaderRoot,
  BreadcrumbHeaderTrail,
  BREADCRUMB_HEADER_CHIP_CLASS,
} from "../../components/BreadcrumbHeaderRoot";
import { PageHeader } from "../../components/PageHeader";
import { useAnalytics } from "../../utils/analytics";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import {
  useCreateAskPunttChat,
  useAskPunttChatHistory,
  useMarkAskPunttArtifactSeen,
  ASK_PUNTT_UNREAD_LIST_POLL_MS,
} from "../../utils/queries/askPuntt";
import {
  askPunttProjectKeys,
  useAskPunttProject,
  useProjectReviews,
  useProjectTimeline,
  useResolveFeedSuggestion,
  useShareProjectChat,
  useUpdateAskPunttProject,
} from "../../utils/queries/askPunttProjects";
import { setDrawerOpen } from "../../utils/slices/ui";

// Route for the Projects launcher (back-link target). Kept as a literal so
// this component imports no route module — that keeps it embeddable and lets
// its test mock only @tanstack/react-router (no sibling-module mocks).
const PROJECTS_LAUNCHER_PATH = "/ask-puntt/projects";

type ProjectTab = "feed" | "chats";

/** Sentinel id for the inline new-chat draft (no persisted chat yet). */
export const NEW_CHAT_DRAFT_ID = "__new__";

export default function AskPunttCampaign() {
  const { t } = useTranslation("ui");
  // Loose param read so the component carries no route-module import (mirrors
  // AskPunttChat). The campaign route always supplies `projectId`.
  const routeParams = useParams({ strict: false }) as {
    projectId: string;
    ticketId?: string;
    commentId?: string;
    chatId?: string;
  };
  const { projectId } = routeParams;
  const urlSearch = useSearch({ strict: false }) as CampaignSearch;
  const navigate = useNavigate();
  const analytics = useAnalytics("ask_puntt_campaign");
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const appSidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);

  /** Ephemeral chat selection not represented in the URL (e.g. new-chat draft). */
  const [localChatOverride, setLocalChatOverride] = useState<string | null>(
    null,
  );
  /** Set when the user explicitly opens Project feed; blocks ?chatId= re-opening My chats. */
  const userChoseFeedRef = useRef(false);

  const activeTab: ProjectTab =
    urlSearch.tab ??
    (routeParams.chatId || urlSearch.chatId ? "chats" : "feed");
  const activeTicketId = routeParams.ticketId ?? urlSearch.review ?? null;
  const activeCommentId = routeParams.commentId ?? null;
  const activeChatId =
    localChatOverride ?? routeParams.chatId ?? urlSearch.chatId ?? null;

  // Poll the project on the feed tab so the Documents & context panel reflects
  // files the agent adds without a manual refresh (it otherwise renders a stale
  // snapshot while the reviews panel polls independently).
  const { data, isLoading, isError } = useAskPunttProject(projectId, {
    refetchInterval:
      activeTab === "feed"
        ? FEED_POLL_INTERVAL_MS
        : ASK_PUNTT_UNREAD_LIST_POLL_MS,
  });
  const createChat = useCreateAskPunttChat();
  const queryClient = useQueryClient();

  const timelineQuery = useProjectTimeline(projectId, {
    enabled: activeTab === "feed",
    refetchInterval: (query) => {
      if (activeTab !== "feed") return false;
      const events = query.state.data?.events ?? [];
      return resolveFeedTimelinePollIntervalMs({
        hasPendingAssistant: timelineHasPendingAssistant(events),
        hasActiveChat: Boolean(activeChatId),
      });
    },
  });
  const reviewsQuery = useProjectReviews(projectId, {
    refetchInterval: activeTab === "feed" ? FEED_POLL_INTERVAL_MS : false,
  });
  const resolveSuggestion = useResolveFeedSuggestion(projectId);
  const updateProject = useUpdateAskPunttProject(projectId);
  const shareChat = useShareProjectChat(projectId);

  // The shared-chat agent can mutate the project (description/context/files).
  // When the embedded chat reports a project-mutating turn, refetch the project
  // + feed so the panels update without a manual refresh.
  const handleProjectMutated = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: askPunttProjectKeys.project(projectId),
    });
    queryClient.invalidateQueries({
      queryKey: askPunttProjectKeys.feed(projectId),
    });
    queryClient.invalidateQueries({
      queryKey: askPunttProjectKeys.timeline(projectId),
    });
    // A project-mutating turn (e.g. a new create_ticket review) also refreshes
    // the right-hand Reviews panel so the new ticket bubble appears at once.
    queryClient.invalidateQueries({
      queryKey: askPunttProjectKeys.reviews(projectId),
    });
  }, [queryClient, projectId]);

  // Canvas viewer is ephemeral UI state (not URL-backed on project routes yet).
  const [activeCanvasMessageId, setActiveCanvasMessageId] = useState<
    string | null
  >(null);
  const [activeChatArtifacts, setActiveChatArtifacts] = useState<
    ChatArtifact[]
  >([]);
  // Lets the user collapse the narrow feed column while reviewing to give the
  // artwork more room.
  const [reviewChatCollapsed, setReviewChatCollapsed] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDescription, setEditDescription] = useState("");
  const [reviewsSectionOpen, setReviewsSectionOpen] = useState(true);
  const [contextSectionOpen, setContextSectionOpen] = useState(true);
  /** In-chat approval card hover/focus → emphasize that file-version in Reviews. */
  const [highlightedApprovalVersionId, setHighlightedApprovalVersionId] =
    useState<string | null>(null);

  const project = data?.project;
  const chats = useMemo(() => data?.chats ?? [], [data?.chats]);
  const sharedChatId =
    timelineQuery.data?.sharedChatId ?? project?.sharedChatId ?? null;

  // Project-only feed events to interleave into the live shared chat on the
  // Project feed tab. Agent turns are excluded — they render as the shared
  // chat's real messages (full streaming/steps/canvas) via the embedded
  // AskPunttChat, so they must not be duplicated as static cards.
  const feedExtras = useMemo(
    () =>
      (timelineQuery.data?.events ?? [])
        .filter((e) => !isAgentTurnEvent(e))
        .map((e) => ({
          id: e._id,
          createdAt: e.createdAt,
          node: renderProjectFeedCard(e, {
            onResolveSuggestion: (eventId, action, payload) =>
              resolveSuggestion.mutate({ eventId, action, payload }),
            resolvingEventId: resolveSuggestion.isPending
              ? resolveSuggestion.variables?.eventId
              : null,
            onAskPrivately: () => openMyChatsTab(),
          }),
        }))
        .filter(
          (x): x is { id: string; createdAt: string; node: JSX.Element } =>
            Boolean(x.node),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      timelineQuery.data?.events,
      resolveSuggestion.isPending,
      resolveSuggestion.variables?.eventId,
    ],
  );

  // Reference documents: user-uploaded project files only. Active review
  // cards stay in Reviews.
  const referenceDocuments = useMemo(
    () => projectReferenceDocumentRows(data?.project?.files),
    [data?.project?.files],
  );

  const reviewsPanelData = useMemo(() => {
    const raw = reviewsQuery.data;
    if (!raw) return undefined;
    return {
      ...raw,
      reviews: enrichProjectReviewsFromFiles(raw.reviews, data?.project?.files),
    };
  }, [reviewsQuery.data, data?.project?.files]);

  const approvalRollupSignal =
    project?.approvalRollup &&
    typeof project.approvalRollup === "object" &&
    "signal" in project.approvalRollup
      ? (project.approvalRollup as { signal?: string }).signal
      : null;

  const syncProjectSearch = useCallback(
    (patch: Partial<CampaignSearch>, options?: { replace?: boolean }) => {
      navigate({
        ...buildProjectFeedPath(projectId, { ...urlSearch, ...patch }),
        replace: options?.replace ?? true,
      });
    },
    [navigate, projectId, urlSearch],
  );

  const navigateProjectReview = useCallback(
    (
      ticketId: string,
      commentId?: string | null,
      options?: { replace?: boolean },
    ) => {
      const preservedPersonalChatId =
        routeParams.chatId ?? urlSearch.chatId ?? undefined;
      const preservePersonalChatInReview =
        preservedPersonalChatId != null &&
        (urlSearch.tab === "chats" || routeParams.chatId != null);

      const location = buildProjectReviewPath(
        projectId,
        ticketId,
        commentId ?? undefined,
        urlSearch.version,
      );

      navigate({
        ...location,
        search: parseProjectReviewRouteSearch({
          ...location.search,
          ...(preservePersonalChatInReview
            ? { tab: "chats", chatId: preservedPersonalChatId }
            : {}),
        }),
        replace: options?.replace ?? false,
      });
    },
    [
      navigate,
      projectId,
      routeParams.chatId,
      urlSearch.chatId,
      urlSearch.tab,
      urlSearch.version,
    ],
  );

  const openProjectReview = useCallback(
    (ticketId: string) => {
      navigateProjectReview(ticketId, null, { replace: false });
    },
    [navigateProjectReview],
  );

  const handleProjectReviewCommentDeepLink = useCallback(
    (commentId: string | null) => {
      if (!activeTicketId) return;
      navigateProjectReview(activeTicketId, commentId, { replace: false });
    },
    [activeTicketId, navigateProjectReview],
  );

  const stripInvalidProjectReviewComment = useCallback(() => {
    if (!activeTicketId) return;
    navigateProjectReview(activeTicketId, null, { replace: true });
  }, [activeTicketId, navigateProjectReview]);

  const closeProjectReview = useCallback(() => {
    navigate({
      ...buildProjectFeedPath(projectId, {
        tab: activeTab,
        version: undefined,
        review: undefined,
      }),
      replace: false,
    });
  }, [activeTab, navigate, projectId]);

  const selectPersonalChat = useCallback(
    (chatId: string) => {
      setLocalChatOverride(null);
      navigate({
        ...buildProjectChatPath(projectId, chatId),
        replace: false,
      });
    },
    [navigate, projectId],
  );

  const showProjectFeed = useCallback(() => {
    userChoseFeedRef.current = true;
    setLocalChatOverride(null);
    navigate({
      ...buildProjectFeedPath(projectId, {
        tab: "feed",
        chatId: undefined,
        review: undefined,
        version: undefined,
      }),
      replace: false,
    });
  }, [navigate, projectId]);
  const navigateProjectRoot = useCallback(() => {
    userChoseFeedRef.current = true;
    setLocalChatOverride(null);
    navigate({
      ...buildProjectFeedPath(projectId),
      replace: false,
    });
  }, [navigate, projectId]);

  const openMyChatsTab = useCallback(() => {
    userChoseFeedRef.current = false;
    setLocalChatOverride(null);
    navigate({
      ...buildProjectFeedPath(projectId, {
        tab: "chats",
        chatId: undefined,
        review: undefined,
        version: undefined,
      }),
      replace: false,
    });
  }, [navigate, projectId]);

  const backToMyChatsList = useCallback(() => {
    setLocalChatOverride(null);
    navigate({
      ...buildProjectFeedPath(projectId, {
        tab: "chats",
        chatId: undefined,
      }),
      replace: false,
    });
  }, [navigate, projectId]);

  // My chats is master-detail: clear ephemeral chat state when the project changes.
  useEffect(() => {
    setLocalChatOverride(null);
    setReviewChatCollapsed(false);
    userChoseFeedRef.current = false;
  }, [projectId]);

  // Strip invalid deep-link ids once project chats are loaded.
  useEffect(() => {
    const chatId = routeParams.chatId ?? urlSearch.chatId?.trim();
    if (!chatId || chats.length === 0) return;
    if (chats.some((c) => c._id === chatId)) return;
    if (routeParams.chatId) {
      navigate({
        ...buildProjectFeedPath(projectId, { tab: "chats" }),
        replace: true,
      });
      return;
    }
    syncProjectSearch({ chatId: undefined }, { replace: true });
  }, [
    routeParams.chatId,
    urlSearch.chatId,
    chats,
    syncProjectSearch,
    navigate,
    projectId,
  ]);

  // Strip invalid review id once reviews are loaded.
  useEffect(() => {
    const reviewId = activeTicketId?.trim();
    if (!reviewId || !reviewsPanelData?.reviews) return;
    if (reviewsPanelData.reviews.some((r) => r.ticketId === reviewId)) return;
    if (routeParams.ticketId) {
      navigate({
        ...buildProjectFeedPath(projectId, { tab: activeTab }),
        replace: true,
      });
      return;
    }
    syncProjectSearch(
      { review: undefined, version: undefined },
      { replace: true },
    );
  }, [
    activeTab,
    activeTicketId,
    navigate,
    projectId,
    reviewsPanelData?.reviews,
    routeParams.ticketId,
    syncProjectSearch,
  ]);

  useEffect(() => {
    if (projectId)
      analytics.capture("ask_puntt_campaign_viewed", { projectId });
  }, [projectId]);

  const handleCreatePersonalChat = async (
    message: string,
    attachments?: {
      key: string;
      source: string;
      type: string;
      name?: string;
    }[],
  ) => {
    const msg = message.trim();
    if (!msg && (!attachments || attachments.length === 0)) return;
    try {
      const result = await createChat.mutateAsync({
        message: msg,
        workflowType: "general",
        projectId,
        projectChatKind: "personal",
        attachments: attachments?.length ? attachments : undefined,
      });
      // Fire the shared chat_created event too — PostHog insights counting
      // ask_puntt_chat_created were blind to project chats when only the
      // campaign-specific event below fired.
      analytics.capture("ask_puntt_chat_created", {
        workflow_type: "general",
        message_length: msg.length,
        has_attachments: (attachments?.length ?? 0) > 0,
        attachment_count: attachments?.length ?? 0,
        attachment_ids: attachments?.map((att) => att.key) ?? [],
        project_id: projectId,
        project_chat_kind: "personal",
        is_new_chat: true,
      });
      analytics.capture("ask_puntt_campaign_personal_chat_created", {
        projectId,
      });
      selectPersonalChat(result.chat._id);
    } catch (error) {
      console.error("Failed to create personal chat:", error);
    }
  };

  const documentsArtifactChatId =
    activeTab === "chats" && activeChatId && activeChatId !== NEW_CHAT_DRAFT_ID
      ? activeChatId
      : sharedChatId;

  // Only poll full chat history while an assistant turn is in flight. Polling
  // every 2s on an idle feed (#4073) kept sessions heavy in prod with nothing
  // running — mirror the timeline's pending gate instead.
  const feedHasPendingAssistant = timelineHasPendingAssistant(
    timelineQuery.data?.events ?? [],
  );

  const { data: documentsArtifactChatData } = useAskPunttChatHistory(
    documentsArtifactChatId ?? "",
    {
      enabled: Boolean(documentsArtifactChatId),
      refetchInterval:
        activeTab === "feed" && feedHasPendingAssistant
          ? FEED_AGENT_POLL_INTERVAL_MS
          : false,
    },
  );

  const markArtifactSeenMutation = useMarkAskPunttArtifactSeen(
    documentsArtifactChatId,
  );

  const handleOpenTicket = useCallback(
    (ticketId: string) => {
      const reviewItem = reviewsPanelData?.reviews?.find(
        (r) => r.ticketId === ticketId,
      );
      if (reviewItem?.status === "in_review") return;
      setActiveCanvasMessageId(null);
      openProjectReview(ticketId);
      markArtifactSeenMutation.mutate({ kind: "review", ticketId });
    },
    [markArtifactSeenMutation, openProjectReview, reviewsPanelData?.reviews],
  );

  const handleProjectCanvasOpen = useCallback(
    (messageId: string) => {
      setActiveCanvasMessageId(messageId);
      if (activeTicketId) {
        navigate({
          ...buildProjectFeedPath(projectId, {
            tab: activeTab,
            version: undefined,
            review: undefined,
          }),
          replace: false,
        });
      }
      markArtifactSeenMutation.mutate({ kind: "canvas", messageId });
    },
    [activeTab, activeTicketId, markArtifactSeenMutation, navigate, projectId],
  );

  const handleCloseCanvasViewer = useCallback(() => {
    setActiveCanvasMessageId(null);
  }, []);

  const activeChatCanvasArtifacts = useMemo(() => {
    const fromEmbeddedChat = activeChatArtifacts.filter(
      (a) => a.kind === "canvas",
    );
    if (fromEmbeddedChat.length > 0) return fromEmbeddedChat;
    const messages = documentsArtifactChatData?.messages;
    if (!messages?.length) return [];
    return collectChatArtifacts(messages).filter((a) => a.kind === "canvas");
  }, [activeChatArtifacts, documentsArtifactChatData?.messages]);

  const projectChatUsedConnectors = useMemo(
    // Shared project chat only — personal project chats are not included.
    () => collectUsedConnectors(documentsArtifactChatData?.messages),
    [documentsArtifactChatData?.messages],
  );

  const reviewTicketUsedConnectors = useMemo(
    () =>
      activeTicketId
        ? collectUsedConnectors(documentsArtifactChatData?.messages, {
            ticketId: activeTicketId,
          })
        : [],
    [documentsArtifactChatData?.messages, activeTicketId],
  );

  const artifactChatId = documentsArtifactChatId;

  const { data: artifactChatData } = useAskPunttChatHistory(
    artifactChatId ?? "",
    {
      enabled: Boolean(artifactChatId) && Boolean(activeCanvasMessageId),
    },
  );

  const unseenArtifactKeys = useMemo(
    () =>
      new Set(
        documentsArtifactChatData?.userSeen?.unseenArtifactKeys ??
          artifactChatData?.userSeen?.unseenArtifactKeys ??
          [],
      ),
    [
      documentsArtifactChatData?.userSeen?.unseenArtifactKeys,
      artifactChatData?.userSeen?.unseenArtifactKeys,
    ],
  );

  const isCampaignArtifactUnread = useCallback(
    (artifact: ChatArtifact) => isArtifactUnread(artifact, unseenArtifactKeys),
    [unseenArtifactKeys],
  );

  const isCampaignReviewUnread = useCallback(
    (ticketId: string) => unseenArtifactKeys.has(`review:${ticketId}`),
    [unseenArtifactKeys],
  );

  const activeCampaignCanvas = useMemo(() => {
    if (!activeCanvasMessageId || !artifactChatData?.messages) return null;
    const msg = artifactChatData.messages.find(
      (m) => m._id === activeCanvasMessageId,
    );
    if (
      !msg?.canvasContent ||
      !Array.isArray(msg.canvasContent.blocks) ||
      msg.canvasContent.blocks.length === 0
    ) {
      return null;
    }
    return {
      canvas: msg.canvasContent as CanvasContent,
      skill: msg.activeSkill ?? undefined,
    };
  }, [activeCanvasMessageId, artifactChatData?.messages]);

  const rightRailExpanded =
    reviewsSectionOpen ||
    contextSectionOpen ||
    activeChatCanvasArtifacts.length > 0;

  // Collapse the app nav sidebar when entering review mode (same as 3-pane chat).
  const prevInReviewRef = useRef(false);
  useEffect(() => {
    const inArtifactViewer = Boolean(activeTicketId || activeCanvasMessageId);
    if (inArtifactViewer && !prevInReviewRef.current && appSidebarOpen) {
      dispatch(setDrawerOpen({ app: false }));
    }
    prevInReviewRef.current = inArtifactViewer;
  }, [activeTicketId, activeCanvasMessageId, appSidebarOpen, dispatch]);

  const handleSaveEdit = async () => {
    try {
      await updateProject.mutateAsync({ description: editDescription });
      setEditOpen(false);
    } catch (error) {
      console.error("Failed to update project context:", error);
    }
  };

  const members = project?.members ?? [];
  const owner = members.find((m) => m._id === project?.ownerId);
  // `project.role` is set by christo to "owner"/"member", but the field is
  // schema-optional and can be absent on the single-project GET. Mirror the
  // chat header's robust check (`chat.userId === currentUserId`) by also
  // comparing ids so owner affordances (comment editing, owner badge) stay
  // available even when `role` is omitted. (Project rename moved to the
  // left-nav Recents rows — gated by canDeleteProject there.)
  const isOwner =
    !!project &&
    (project.role === "owner" ||
      (!!currentUserId && project.ownerId === currentUserId));
  const ownerName = project
    ? isOwner
      ? "You"
      : (owner?.name ?? owner?.email ?? "Owner")
    : "";
  const viewer = members.find((m) => m._id === currentUserId);

  const myChats = chats.filter((c) => c._id !== sharedChatId);
  const activeChat = activeChatId
    ? activeChatId === NEW_CHAT_DRAFT_ID
      ? null
      : (chats.find((c) => c._id === activeChatId) ?? null)
    : null;
  const isDraftChat = activeChatId === NEW_CHAT_DRAFT_ID;
  const isPrivateChatDetail =
    isDraftChat || activeChat?.projectChatKind === "personal";

  const feedChat = sharedChatId ? (
    <AskPunttChat
      key={sharedChatId}
      chatId={sharedChatId}
      projectId={projectId}
      onProjectMutated={handleProjectMutated}
      onProjectReviewOpen={handleOpenTicket}
      onProjectCanvasOpen={handleProjectCanvasOpen}
      onArtifactsChange={setActiveChatArtifacts}
      onApprovalCardHighlight={(target) =>
        setHighlightedApprovalVersionId(target?.versionId ?? null)
      }
      timelineExtras={feedExtras}
      messagePresentation="feed"
      feedActorName={viewer?.name ?? viewer?.email ?? undefined}
      feedActorAvatar={viewer?.avatar ?? undefined}
      feedMembers={members}
      suppressRightPane
    />
  ) : (
    <div
      className="flex flex-1 items-center justify-center"
      data-testid="project-feed-loading"
    >
      <CircleNotch
        size={24}
        weight="bold"
        className="animate-spin text-puntt-neutral-gray-9"
      />
    </div>
  );

  const reviewLeftChat = useMemo(() => {
    if (activeTab === "chats" && activeChatId) {
      if (isDraftChat) {
        return (
          <CampaignNewChatDraft
            data-testid="campaign-new-chat-draft"
            onSubmit={handleCreatePersonalChat}
            isSubmitting={createChat.isPending}
          />
        );
      }
      return (
        <AskPunttChat
          key={activeChatId}
          chatId={activeChatId}
          onProjectMutated={handleProjectMutated}
          onProjectReviewOpen={handleOpenTicket}
          onProjectCanvasOpen={handleProjectCanvasOpen}
          onArtifactsChange={setActiveChatArtifacts}
          feedMembers={members}
          suppressRightPane
        />
      );
    }
    return feedChat;
  }, [
    activeTab,
    activeChatId,
    isDraftChat,
    feedChat,
    createChat.isPending,
    handleProjectMutated,
    handleOpenTicket,
  ]);

  const reviewLeftChatLabel =
    activeTab === "chats" && activeChat && !isDraftChat
      ? (activeChat.title ?? "Chat")
      : "Chat";

  // ---- Resizable review layout (chat | artwork) — mirrors AskPuntt chat.tsx ----
  const reviewPaneContainerRef = useRef<HTMLDivElement | null>(null);
  const reviewContainerWidth = useContainerWidth(
    reviewPaneContainerRef,
    Boolean(activeTicketId),
  );
  const reviewWidthOk =
    reviewContainerWidth === null
      ? false
      : reviewContainerWidth >= RESIZE_LAYOUT_MIN_WIDTH_PX;
  const useResizableReviewLayout =
    Boolean(activeTicketId) && !reviewChatCollapsed && reviewWidthOk;
  const { prefs: uiPrefs, setPref: setUiPref } = useUiPrefs();
  const [reviewActiveDragId, setReviewActiveDragId] = useState<string | null>(
    null,
  );
  const reviewPanelGroupRef = useRef<ImperativePanelGroupHandle>(null);
  const REVIEW_2PANE_DEFAULT: number[] = [36, 64];
  const REVIEW_2PANE_MINS: number[] = [18, 40];
  const reviewTwoPaneLayout = usePersistedLayout({
    storageKey: "askpuntt-project-review-2pane-v2",
    defaultLayout: REVIEW_2PANE_DEFAULT,
    minSizes: REVIEW_2PANE_MINS,
  });
  const reviewDragAbortSignal = `${activeTicketId ?? ""}:${
    reviewChatCollapsed ? "collapsed" : "expanded"
  }`;
  const reviewDragInProgress = reviewActiveDragId !== null;

  // ---- Resizable default project layout (main | right rail + reviews | context) ----
  const feedWorkspaceRef = useRef<HTMLDivElement | null>(null);
  const feedPanelGroupRef = useRef<ImperativePanelGroupHandle>(null);
  const [feedActiveDragId, setFeedActiveDragId] = useState<string | null>(null);
  // The workspace row (and its ref) only mount after project data loads and
  // while the default feed view is active. Without `enabled`, the first effect
  // run sees ref.current === null during the loading spinner and never re-runs.
  const feedWorkspaceReady =
    !isLoading && project != null && activeTicketId == null;
  const feedContainerWidth = useContainerWidth(
    feedWorkspaceRef,
    feedWorkspaceReady,
  );
  const feedWidthOk =
    feedContainerWidth === null
      ? false
      : feedContainerWidth >= RESIZE_LAYOUT_MIN_WIDTH_PX;
  const useResizableFeedLayout = rightRailExpanded && feedWidthOk;
  const feedRailLayout = usePersistedLayout({
    storageKey: "askpuntt-project-feed-rail-v1",
    defaultLayout: FEED_RAIL_DEFAULT,
    minSizes: FEED_RAIL_MINS,
  });
  const feedDragInProgress = feedActiveDragId !== null;
  const feedRailDragAbortSignal = `${projectId}:${rightRailExpanded ? "rail" : "collapsed"}`;

  const reviewChatPanelInner = (
    <>
      <div
        className={`flex ${PANE_HEADER_HEIGHT_CLASS} ${PANE_HEADER_SURFACE_CLASS} ${PANE_HEADER_SIDE_INSET_CLASS} shrink-0 items-center gap-2 border-b border-puntt-neutral-gray-6`}
      >
        <h2
          className="flex-1 truncate text-[11px] font-bold uppercase tracking-wide text-puntt-neutral-gray-11"
          data-testid="campaign-review-chat-heading"
        >
          {reviewLeftChatLabel}
        </h2>
        <Tooltip content="Collapse chat">
          <PaneHeaderIconButton
            onClick={() => setReviewChatCollapsed(true)}
            aria-label={t("collapseChat")}
            data-testid="campaign-review-chat-collapse"
          >
            <ArrowsInSimple size={PANE_HEADER_ICON_SIZE} aria-hidden="true" />
          </PaneHeaderIconButton>
        </Tooltip>
      </div>
      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid="campaign-review-chat-body"
      >
        {activeTab === "chats" && isPrivateChatDetail && (
          <CampaignChatPrivacyBanner />
        )}
        <div className="min-h-0 flex-1 overflow-hidden">{reviewLeftChat}</div>
      </div>
    </>
  );

  if (isLoading) {
    return (
      <div
        className="flex h-full items-center justify-center bg-base-white"
        data-testid="campaign-loading"
      >
        <CircleNotch
          size={28}
          weight="bold"
          className="animate-spin text-puntt-neutral-gray-9"
        />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center gap-4 bg-base-white"
        data-testid="campaign-error"
      >
        <Text size="4" weight="medium" className="text-puntt-neutral-gray-11">
          {t("campaign.couldNotLoadProject")}
        </Text>
        <Link
          to={PROJECTS_LAUNCHER_PATH}
          className="text-sm text-puntt-accent-9 hover:underline"
          data-testid="campaign-error-back"
        >
          {t("campaign.backToAllProjects")}
        </Link>
      </div>
    );
  }

  const openEdit = () => {
    setEditDescription(project.description ?? "");
    setEditOpen(true);
  };

  const activeReview = reviewsPanelData?.reviews?.find(
    (r) => r.ticketId === activeTicketId,
  );
  const activeReviewHasContext = (activeReview?.contextAssets?.length ?? 0) > 0;
  const reviewWorkspaceMinWidthPx = activeTicketId
    ? reviewWorkspaceMinWidth({
        chatCollapsed: reviewChatCollapsed,
        contextCollapsed: false,
        hasContext: activeReviewHasContext,
      })
    : undefined;

  return (
    <div
      className="flex size-full min-h-0 min-w-0 flex-col overflow-hidden"
      data-testid="ask-puntt-campaign"
    >
      <PageHeader className="w-full min-w-0 max-w-full gap-3 overflow-hidden border-puntt-neutral-gray-5">
        <BreadcrumbHeaderTrail className="min-w-0 flex-1 gap-6 overflow-hidden">
          <BreadcrumbHeaderRoot
            to={PROJECTS_LAUNCHER_PATH}
            label={t("allProjects")}
            testId="project-breadcrumb-all-projects"
          />
          <ProjectBreadcrumbTitle
            name={project.name}
            onNavigate={navigateProjectRoot}
          />
          <span
            className={cx(
              "hidden shrink-0 rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[11px] font-medium text-puntt-neutral-gray-11 sm:inline",
              BREADCRUMB_HEADER_CHIP_CLASS,
            )}
            data-testid="campaign-owner-badge"
          >
            {t("ownedBy")} {ownerName}
          </span>
        </BreadcrumbHeaderTrail>
        {members.length > 0 && (
          <div
            className="flex shrink-0 items-center -space-x-2"
            data-testid="campaign-members"
            aria-label={t("campaign.projectMembers", {
              count: members.length,
            })}
          >
            {members.slice(0, 5).map((m) => (
              <AvatarWithInitials
                key={m._id}
                size={7}
                name={m.name ?? m.email ?? "Member"}
                avatar={m.avatar ?? undefined}
                colorKey={m._id}
                backgroundColor={m.avatarBackgroundColor ?? undefined}
                textColor={m.avatarTextColor ?? undefined}
                className="ring-2 ring-puntt-neutral-gray-1"
              />
            ))}
            {members.length > 5 && (
              <span
                className="flex size-7 items-center justify-center rounded-full bg-puntt-neutral-gray-3 text-[10px] font-medium text-puntt-neutral-gray-11 ring-2 ring-puntt-neutral-gray-1"
                data-testid="campaign-members-overflow"
              >
                +{members.length - 5}
              </span>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          aria-label={t("inviteToProject")}
          data-testid="project-invite-button"
          className="flex shrink-0 items-center gap-1.5 rounded-md border border-puntt-neutral-gray-7 bg-base-white px-2.5 py-1.5 text-[13px] font-medium text-puntt-neutral-gray-12 transition-colors hover:border-puntt-accent-9 hover:bg-puntt-blue-2 hover:text-puntt-accent-9"
        >
          <UserPlus size={15} />
          {t("invite")}
        </button>
      </PageHeader>

      {!activeTicketId && !activeCanvasMessageId && (
        <div
          role="tablist"
          aria-label={t("projectViews")}
          className="flex shrink-0 gap-8 border-b border-puntt-neutral-gray-5 bg-base-white px-5"
        >
          <TabButton
            label={t("sharedProjectFeed")}
            data-testid="project-feed-tab"
            active={activeTab === "feed"}
            onClick={showProjectFeed}
          />
          <TabButton
            label={t("privateChats")}
            data-testid="project-my-chats-tab"
            active={activeTab === "chats"}
            onClick={openMyChatsTab}
          />
        </div>
      )}

      {activeTicketId ? (
        <div
          className="flex min-h-0 w-full min-w-0 flex-1 overflow-x-auto overflow-y-hidden"
          data-testid="campaign-body-scroll"
          aria-label={t("projectWorkspace")}
        >
          <div
            className="flex size-full min-h-0"
            data-testid="campaign-workspace-row"
            style={
              reviewWorkspaceMinWidthPx != null
                ? reviewWorkspaceRowStyle(reviewWorkspaceMinWidthPx)
                : undefined
            }
          >
            <div
              ref={reviewPaneContainerRef}
              className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden"
              data-testid="campaign-review-mode"
            >
              {useResizableReviewLayout ? (
                <PanelGroup
                  direction="horizontal"
                  ref={reviewPanelGroupRef}
                  onLayout={reviewTwoPaneLayout.onLayout}
                  className={`flex flex-1 ${reviewDragInProgress ? "[&_*]:!transition-none" : ""}`}
                >
                  <Panel
                    id="chat"
                    order={0}
                    minSize={REVIEW_2PANE_MINS[0]}
                    defaultSize={reviewTwoPaneLayout.layout[0]}
                  >
                    <div
                      className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
                      data-testid="pane-chat"
                    >
                      {reviewChatPanelInner}
                    </div>
                  </Panel>
                  <ResizeHandle
                    id="chat-right"
                    panelGroupRef={reviewPanelGroupRef}
                    defaultLayout={REVIEW_2PANE_DEFAULT}
                    abortSignal={reviewDragAbortSignal}
                    onDraggingChange={(d) =>
                      setReviewActiveDragId(d ? "chat-right" : null)
                    }
                  />
                  <Panel
                    id="review"
                    order={1}
                    minSize={REVIEW_2PANE_MINS[1]}
                    defaultSize={reviewTwoPaneLayout.layout[1]}
                  >
                    <CampaignReviewPane
                      ticketId={activeTicketId}
                      onClose={closeProjectReview}
                      initialCommentId={activeCommentId}
                      onCommentDeepLinkChange={
                        handleProjectReviewCommentDeepLink
                      }
                      onInvalidCommentDeepLink={
                        stripInvalidProjectReviewComment
                      }
                      usedConnectors={reviewTicketUsedConnectors}
                    />
                  </Panel>
                </PanelGroup>
              ) : (
                <>
                  {reviewChatCollapsed ? (
                    <button
                      type="button"
                      onClick={() => setReviewChatCollapsed(false)}
                      aria-label={t("expandChat")}
                      aria-expanded={false}
                      data-testid="campaign-review-chat-expand"
                      className="flex w-8 shrink-0 flex-col items-center gap-2 border-r border-puntt-neutral-gray-5 bg-puntt-neutral-gray-1 py-3 text-puntt-neutral-gray-11 transition-colors hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-12"
                    >
                      <ArrowsOutSimple size={14} aria-hidden="true" />
                      <span className="text-[11px] font-bold uppercase tracking-wide [writing-mode:vertical-rl]">
                        {t("chat")}
                      </span>
                    </button>
                  ) : (
                    <div
                      className="flex shrink-0 flex-col border-r border-puntt-neutral-gray-5"
                      style={{
                        width: `min(${REVIEW_CHAT_FALLBACK_PX}px, 30vw)`,
                      }}
                    >
                      {reviewChatPanelInner}
                    </div>
                  )}
                  <CampaignReviewPane
                    ticketId={activeTicketId}
                    onClose={closeProjectReview}
                    initialCommentId={activeCommentId}
                    onCommentDeepLinkChange={handleProjectReviewCommentDeepLink}
                    onInvalidCommentDeepLink={stripInvalidProjectReviewComment}
                    usedConnectors={reviewTicketUsedConnectors}
                  />
                </>
              )}
              <ResizeHintTooltip
                alreadySeen={Boolean(uiPrefs.resizeHintSeen)}
                onShown={() => setUiPref("resizeHintSeen", true)}
                resizableActive={useResizableReviewLayout}
              />
            </div>
          </div>
        </div>
      ) : activeCanvasMessageId && activeCampaignCanvas ? (
        <div
          className="flex min-h-0 w-full min-w-0 flex-1 overflow-hidden"
          data-testid="campaign-canvas-view-mode"
          aria-label={t("projectCanvasWorkspace")}
        >
          <div className="flex size-full min-h-0 min-w-0">
            <div
              className="flex min-h-0 min-w-0 flex-col overflow-hidden border-r border-puntt-neutral-gray-5"
              style={{ width: `min(${REVIEW_CHAT_FALLBACK_PX}px, 30vw)` }}
              data-testid="pane-chat"
            >
              {reviewLeftChat}
            </div>
            <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
              <CanvasPane
                canvas={activeCampaignCanvas.canvas}
                onDismiss={handleCloseCanvasViewer}
                skillName={
                  activeCampaignCanvas.skill
                    ? activeCampaignCanvas.skill
                    : undefined
                }
                chatId={artifactChatId ?? undefined}
                leadingClose={{ ariaLabel: "Close document" }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className="flex min-h-0 flex-1 overflow-hidden"
          data-testid="campaign-body-scroll"
          aria-label={t("projectWorkspace")}
        >
          <div
            ref={feedWorkspaceRef}
            className="flex size-full min-h-0 min-w-0"
            data-testid="campaign-workspace-row"
          >
            {rightRailExpanded && useResizableFeedLayout ? (
              <PanelGroup
                direction="horizontal"
                ref={feedPanelGroupRef}
                onLayout={feedRailLayout.onLayout}
                className={`flex flex-1 ${feedDragInProgress ? "[&_*]:!transition-none" : ""}`}
              >
                <Panel
                  id="main"
                  order={0}
                  minSize={FEED_RAIL_MINS[0]!}
                  defaultSize={feedRailLayout.layout[0]}
                >
                  <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
                    {activeTab === "feed" ? (
                      <div
                        className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                        data-testid="project-feed-panel"
                      >
                        {feedChat}
                      </div>
                    ) : (
                      <div
                        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                        data-testid="my-chats-panel"
                      >
                        {activeChatId ? (
                          <div
                            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                            data-testid="campaign-chat-area"
                          >
                            <CampaignChatDetailHeader
                              title={
                                isDraftChat
                                  ? "New chat"
                                  : (activeChat?.title ?? "Chat")
                              }
                              isShared={
                                activeChat?.projectChatKind === "shared"
                              }
                              onBack={backToMyChatsList}
                              onShare={() => {
                                if (activeChatId && !isDraftChat) {
                                  shareChat.mutate(activeChatId);
                                }
                              }}
                              isSharing={shareChat.isPending}
                              shareDisabled={isDraftChat}
                            />
                            {isPrivateChatDetail && (
                              <CampaignChatPrivacyBanner />
                            )}
                            {isDraftChat ? (
                              <CampaignNewChatDraft
                                data-testid="campaign-new-chat-draft"
                                onSubmit={handleCreatePersonalChat}
                                isSubmitting={createChat.isPending}
                              />
                            ) : (
                              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                <AskPunttChat
                                  key={activeChatId}
                                  chatId={activeChatId}
                                  onProjectMutated={handleProjectMutated}
                                  onProjectReviewOpen={handleOpenTicket}
                                  onProjectCanvasOpen={handleProjectCanvasOpen}
                                  onArtifactsChange={setActiveChatArtifacts}
                                  feedMembers={members}
                                  suppressRightPane
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <CampaignChatList
                            chats={myChats}
                            currentUserId={currentUserId}
                            members={members}
                            onSelect={selectPersonalChat}
                            onNewChat={() => {
                              setLocalChatOverride(NEW_CHAT_DRAFT_ID);
                              syncProjectSearch(
                                { chatId: undefined },
                                { replace: false },
                              );
                            }}
                            isCreating={createChat.isPending}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </Panel>
                <ResizeHandle
                  id="feed-rail"
                  panelGroupRef={feedPanelGroupRef}
                  defaultLayout={FEED_RAIL_DEFAULT}
                  abortSignal={feedRailDragAbortSignal}
                  onDraggingChange={(d) =>
                    setFeedActiveDragId(d ? "feed-rail" : null)
                  }
                />
                <Panel
                  id="rail"
                  order={1}
                  minSize={FEED_RAIL_MINS[1]!}
                  defaultSize={feedRailLayout.layout[1]}
                >
                  <aside
                    className="flex h-full min-h-0 flex-col gap-3 overflow-hidden bg-puntt-neutral-gray-2 p-3"
                    data-testid="campaign-right-rail"
                    data-expanded="true"
                  >
                    <div
                      className={
                        reviewsSectionOpen
                          ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                          : "shrink-0"
                      }
                      data-testid="campaign-reviews-rail-slot"
                      data-section-expanded={
                        reviewsSectionOpen ? "true" : "false"
                      }
                    >
                      <ProjectReviewsPanel
                        data={reviewsPanelData}
                        isLoading={reviewsQuery.isLoading}
                        open={reviewsSectionOpen}
                        onOpenChange={setReviewsSectionOpen}
                        isReviewUnread={isCampaignReviewUnread}
                        onOpenReview={(item) => {
                          if (item.ticketId) handleOpenTicket(item.ticketId);
                        }}
                        approvalRollupSignal={approvalRollupSignal ?? null}
                        project={project}
                        highlightedVersionId={highlightedApprovalVersionId}
                      />
                    </div>
                    {activeChatCanvasArtifacts.length > 0 ? (
                      <div
                        className="flex max-h-[35%] min-h-24 shrink-0 flex-col overflow-hidden"
                        data-testid="campaign-artifacts-documents-panel"
                        data-section-expanded="true"
                      >
                        <ChatArtifactsPanel
                          reviewArtifacts={[]}
                          canvasArtifacts={activeChatCanvasArtifacts}
                          isArtifactActive={(artifact) =>
                            artifact.messageId === activeCanvasMessageId
                          }
                          isArtifactUnread={isCampaignArtifactUnread}
                          onOpenArtifact={(payload) => {
                            if (payload.kind === "canvas") {
                              handleProjectCanvasOpen(payload.messageId);
                            }
                          }}
                          testIdPrefix="campaign-artifacts"
                        />
                      </div>
                    ) : null}
                    <div
                      className={
                        contextSectionOpen
                          ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                          : "shrink-0"
                      }
                      data-testid="campaign-context-rail-slot"
                      data-section-expanded={
                        contextSectionOpen ? "true" : "false"
                      }
                    >
                      <CampaignDocsPanel
                        documents={referenceDocuments}
                        description={project.description}
                        upcomingWork={project.upcomingWork}
                        details={project.agentScratchpad ?? []}
                        usedConnectors={projectChatUsedConnectors}
                        onEdit={isOwner ? openEdit : undefined}
                        panelOpen={contextSectionOpen}
                        onPanelOpenChange={setContextSectionOpen}
                      />
                    </div>
                  </aside>
                </Panel>
              </PanelGroup>
            ) : (
              <>
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                  <div className="flex min-h-0 flex-1 overflow-hidden">
                    {activeTab === "feed" ? (
                      <div
                        className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                        data-testid="project-feed-panel"
                      >
                        {feedChat}
                      </div>
                    ) : (
                      <div
                        className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                        data-testid="my-chats-panel"
                      >
                        {activeChatId ? (
                          <div
                            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                            data-testid="campaign-chat-area"
                          >
                            <CampaignChatDetailHeader
                              title={
                                isDraftChat
                                  ? "New chat"
                                  : (activeChat?.title ?? "Chat")
                              }
                              isShared={
                                activeChat?.projectChatKind === "shared"
                              }
                              onBack={backToMyChatsList}
                              onShare={() => {
                                if (activeChatId && !isDraftChat) {
                                  shareChat.mutate(activeChatId);
                                }
                              }}
                              isSharing={shareChat.isPending}
                              shareDisabled={isDraftChat}
                            />
                            {isPrivateChatDetail && (
                              <CampaignChatPrivacyBanner />
                            )}
                            {isDraftChat ? (
                              <CampaignNewChatDraft
                                data-testid="campaign-new-chat-draft"
                                onSubmit={handleCreatePersonalChat}
                                isSubmitting={createChat.isPending}
                              />
                            ) : (
                              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                <AskPunttChat
                                  key={activeChatId}
                                  chatId={activeChatId}
                                  onProjectMutated={handleProjectMutated}
                                  onProjectReviewOpen={handleOpenTicket}
                                  onProjectCanvasOpen={handleProjectCanvasOpen}
                                  onArtifactsChange={setActiveChatArtifacts}
                                  feedMembers={members}
                                  suppressRightPane
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <CampaignChatList
                            chats={myChats}
                            currentUserId={currentUserId}
                            members={members}
                            onSelect={selectPersonalChat}
                            onNewChat={() => {
                              setLocalChatOverride(NEW_CHAT_DRAFT_ID);
                              syncProjectSearch(
                                { chatId: undefined },
                                { replace: false },
                              );
                            }}
                            isCreating={createChat.isPending}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <CollapsedPaneLabel
                  collapsed={!rightRailExpanded}
                  onExpand={() => {
                    setReviewsSectionOpen(true);
                    setContextSectionOpen(true);
                  }}
                  ariaLabel="Show reviews and project context"
                  testId="campaign-right-rail-expand"
                  labelRender={() => "Context"}
                />
                {rightRailExpanded ? (
                  <aside
                    className="flex h-full min-h-0 shrink-0 flex-col gap-3 overflow-hidden border-l border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 p-3"
                    style={{ width: PROJECT_RAIL_WIDTH_PX }}
                    data-testid="campaign-right-rail"
                    data-expanded="true"
                  >
                    {/* Always mount the reviews panel so its toggle keeps it in the DOM;
                        the panel itself collapses/expands via the controlled open prop. */}
                    <div
                      className={
                        reviewsSectionOpen
                          ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                          : "shrink-0"
                      }
                      data-testid="campaign-reviews-rail-slot"
                      data-section-expanded={
                        reviewsSectionOpen ? "true" : "false"
                      }
                    >
                      <ProjectReviewsPanel
                        data={reviewsPanelData}
                        isLoading={reviewsQuery.isLoading}
                        open={reviewsSectionOpen}
                        onOpenChange={setReviewsSectionOpen}
                        isReviewUnread={isCampaignReviewUnread}
                        onOpenReview={(item) => {
                          if (item.ticketId) handleOpenTicket(item.ticketId);
                        }}
                        approvalRollupSignal={approvalRollupSignal ?? null}
                        project={project}
                        highlightedVersionId={highlightedApprovalVersionId}
                      />
                    </div>
                    {activeChatCanvasArtifacts.length > 0 ? (
                      <div
                        className="flex max-h-[35%] min-h-24 shrink-0 flex-col overflow-hidden"
                        data-testid="campaign-artifacts-documents-panel"
                        data-section-expanded="true"
                      >
                        <ChatArtifactsPanel
                          reviewArtifacts={[]}
                          canvasArtifacts={activeChatCanvasArtifacts}
                          isArtifactActive={(artifact) =>
                            artifact.messageId === activeCanvasMessageId
                          }
                          isArtifactUnread={isCampaignArtifactUnread}
                          onOpenArtifact={(payload) => {
                            if (payload.kind === "canvas") {
                              handleProjectCanvasOpen(payload.messageId);
                            }
                          }}
                          testIdPrefix="campaign-artifacts"
                        />
                      </div>
                    ) : null}
                    {/* Always mount the docs panel so its toggle keeps it in the DOM;
                        the panel itself collapses/expands via the controlled panelOpen. */}
                    <div
                      className={
                        contextSectionOpen
                          ? "flex min-h-0 flex-1 flex-col overflow-hidden"
                          : "shrink-0"
                      }
                      data-testid="campaign-context-rail-slot"
                      data-section-expanded={
                        contextSectionOpen ? "true" : "false"
                      }
                    >
                      <CampaignDocsPanel
                        documents={referenceDocuments}
                        description={project.description}
                        upcomingWork={project.upcomingWork}
                        details={project.agentScratchpad ?? []}
                        usedConnectors={projectChatUsedConnectors}
                        onEdit={isOwner ? openEdit : undefined}
                        panelOpen={contextSectionOpen}
                        onPanelOpenChange={setContextSectionOpen}
                      />
                    </div>
                  </aside>
                ) : null}
              </>
            )}
            <ResizeHintTooltip
              alreadySeen={Boolean(uiPrefs.resizeHintSeen)}
              onShown={() => setUiPref("resizeHintSeen", true)}
              resizableActive={
                useResizableFeedLayout || useResizableReviewLayout
              }
            />
          </div>
        </div>
      )}

      {/* Invite modal */}
      <ProjectInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        projectId={projectId}
        projectName={project.name}
        members={members}
      />

      {/* Edit project context dialog (owner-only) */}
      <Dialog.Root open={editOpen} onOpenChange={setEditOpen}>
        <Dialog.Content
          maxWidth="500px"
          data-testid="project-context-edit-dialog"
          aria-label={t("editProjectContext")}
        >
          <Dialog.Title>{t("editProjectContext")}</Dialog.Title>
          <Dialog.Description size="2" className="text-puntt-neutral-gray-11">
            {t("updateProjectDescription")}
          </Dialog.Description>
          <TextArea
            data-testid="project-context-edit-input"
            aria-label={t("projectDescription")}
            className="mt-3"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            style={{ height: 120 }}
          />
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                data-testid="project-context-edit-cancel"
              >
                {t("cancel")}
              </Button>
            </Dialog.Close>
            <Button
              data-testid="project-context-edit-save"
              disabled={updateProject.isPending}
              onClick={handleSaveEdit}
            >
              {updateProject.isPending ? (
                <CircleNotch size={14} weight="bold" className="animate-spin" />
              ) : null}
              {t("save")}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}

function TabButton({
  label,
  active,
  onClick,
  "data-testid": dataTestid,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  "data-testid": string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={dataTestid}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      className={`-mb-px border-b-[3px] bg-transparent px-0.5 py-3 text-[13px] transition-colors hover:bg-transparent ${
        active
          ? "border-puntt-accent-9 font-semibold text-puntt-accent-9"
          : "border-transparent font-normal text-puntt-neutral-gray-10 hover:text-puntt-neutral-gray-11"
      }`}
    >
      {label}
    </button>
  );
}
